import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { styles } from "./PlaceDiff.style";
import { watch } from "../internal/watch";
import { IPlace } from "../../models/Models";
import {
    DiffKind,
    IModerationRequest,
    IPlaceModerationPayload,
    ModerationTable,
} from "../../backend/cloud/ModerationTable";
import { Diff } from "deep-diff";
import * as AppSyncSchema from "../../AppSyncSchema";
import { ModelField } from "@aws-amplify/datastore";
import { DiffOperation } from "./Controls";

import "./DiffControl";
import { choose } from "lit/directives/choose.js";
import { litIf } from "../LitUtils";
import { PlaceTable } from "../../backend/cloud/PlaceTable";

type DialogType = "confirm-approve" | "confirm-reject";

@customElement("ds-place-diff")
export class PlaceDiff extends BaseElement {
    static override styles = [styles];

    @property({ type: Object }) request?: IModerationRequest;
    @property({ type: Boolean }) showCurrentUser = false;

    @state() protected _place?: IPlace;
    @state() protected _diffs?: IPlaceModerationPayload;
    @state() protected _diffsToKeep: Diff<IPlace>[] = [];
    @state() protected _diffsToReject: Diff<IPlace>[] = [];
    @state() protected _canApprove = false;
    @state() protected _dialog?: DialogType;
    @state() protected _userRequest = false;

    private _places = this._kernel.dataStore.getSource<PlaceTable>("places");
    private _moderation = this._kernel.dataStore.getSource<ModerationTable>("moderation-requests");

    @watch("request")
    async onRequestChanged() {
        this._place = this._diffs = undefined;
        this._diffsToKeep = [];
        if (this.request) {
            this._userRequest = this.request.owner === this._kernel.user?.id;
            this._diffs = JSON.parse(this.request.payload);
            if (this._diffs) {
                this._place = await this._places.read(this._diffs.id);
            }
        }
    }

    private _submit() {
        if (this.request && this._place) {
            this._moderation.acceptPlaceRequest(this.request, this._place.id, this._diffsToKeep);
        }
    }

    private _reject() {
        if (this.request) {
            this._moderation.delete(this.request.id);
        }
    }

    private _update() {
        if (this._diffs) {
            this._canApprove =
                this._diffsToKeep.length + this._diffsToReject.length === this._diffs.diff.length;
        } else {
            this._canApprove = false;
        }
    }

    private _acceptAll() {
        this._diffs?.diff.forEach((x) => this._acceptDiff(x));
        this._diffsToReject = [];
        this._update();
    }

    private _acceptDiff(diff: Diff<IPlace>) {
        if (!this._diffsToKeep.includes(diff)) {
            this._diffsToKeep.push(diff);
        }
        this._update();
    }

    private _rejectDiff(diff: Diff<IPlace>) {
        if (this._diffsToKeep.includes(diff)) {
            this._diffsToKeep = this._diffsToKeep.filter((x) => x !== diff);
        }
        this._update();
    }

    private _renderDiffControl(
        title: string,
        diff: Diff<IPlace>,
        field: ModelField,
        op: DiffOperation,
        value: any
    ) {
        return html`<ds-diff-control
            .heading=${title}
            .field=${field}
            .op=${op}
            .value=${value}
            ?noAcceptReject=${this._userRequest}
            @ds-accept-diff=${() => this._acceptDiff(diff)}
            @ds-reject-diff=${() => this._rejectDiff(diff)}
        ></ds-diff-control>`;
    }

    private _renderDiff(diff: Diff<IPlace>) {
        if (diff.path) {
            const field = AppSyncSchema.getField("Place", diff.path);
            switch (diff.kind) {
                case DiffKind.ArrayChanged: {
                    if (field?.isArray) {
                        if (diff.item.kind === DiffKind.New) {
                            const value = diff.item.rhs;

                            return this._renderDiffControl(
                                `Added ${diff.path[diff.path.length - 1]}`,
                                diff,
                                field,
                                "create",
                                value
                            );
                        }
                    }
                }
            }
            return html`${JSON.stringify(diff, undefined, 2)}`;
        }
        return html`<em>Malformed diff</em>`;
    }

    override render() {
        return html` ${choose(this._currentDialog, [
                [
                    "confirm-accept",
                    () =>
                        html`<ds-confirm-dialog
                            open
                            prompt="Approve submission?"
                            message="The will apply all accepted changes. Press 'Yes' to accept or 'Cancel' to abort."
                            @on-approved=${() => this._submit()}
                            @on-cancel=${(e: Event) => {
                                e.stopPropagation();
                            }}
                            @sl-after-hide=${(e: Event) => {
                                this._dialogAfterHide(e);
                            }}
                        >
                        </ds-confirm-dialog>`,
                ],
                [
                    "confirm-reject",
                    () =>
                        html`<ds-confirm-dialog
                            open
                            prompt="Reject submission?"
                            message="The will reject and delete the submission. Press 'Yes' to reject or 'Cancel' to abort."
                            @on-approved=${() => this._reject()}
                            @on-cancel=${(e: Event) => {
                                e.stopPropagation();
                            }}
                            @sl-after-hide=${(e: Event) => {
                                this._dialogAfterHide(e);
                            }}
                        >
                        </ds-confirm-dialog>`,
                ],
            ])}
            <div class="flex-column">
                <span class="heading">Updated place: ${this._place?.data.name} </span>
                ${this._diffs?.diff.map((d) => this._renderDiff(d))}
                <div slot="footer" class="footer flex-row">
                    ${litIf(
                        !this._userRequest,
                        () => html`
                            <!-- sl-button @click=${() =>
                                this._acceptAll()}>Accept all</sl-button -->
                            <sl-button
                                ?disabled=${!this._canApprove}
                                variant="success"
                                @click=${() => this._openDialog("confirm-accept")}
                                >Approve</sl-button
                            ><sl-button
                                variant="danger"
                                @click=${() => this._openDialog("confirm-reject")}
                                >Reject</sl-button
                            >
                        `
                    )}
                </div>
            </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-place-diff": PlaceDiff;
    }
}
